import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Banner,
    BannerDescription,
    BannerEyebrow,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Button,
    FlexBox,
    FluidImage,
    H1,
    P,
} from '@vp/swan';
import styled from 'styled-components';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import { SupportedLocale, __, __image } from '@99designs/i18n';
import {
    sendProductSearchEngagedTrackingEvent,
    sendProductSearchResultsDisplayedTrackingEvent,
    usePage,
} from '@99designs/tracking';
import { useDesignProductSearch } from '../../../hooks';
import { MIN_SEARCH_QUERY_LENGTH } from '../../../hooks/useDesignProductSearch';
import { getPopularDesignSearchProducts } from '../../../products/getPopularDesignSearchProducts';
import { CloudinaryImage } from '../CloudinaryImage';
import { DesignProductSearch } from '../DesignProductSearch/DesignProductSearch';
import { getProductSearchResultsDisplayedTracking } from './helpers';

interface HeaderProps {
    browseDesignServicesCtaOnClick: () => void;
    howItWorksCtaOnClick: () => void;
    locale: SupportedLocale;
}

const ImageContainer = styled(FlexBox)`
    flex-basis: 50%;
`;

export function Header({
    browseDesignServicesCtaOnClick,
    howItWorksCtaOnClick,
    locale,
}: HeaderProps) {
    const inVariant = useInVistaExperimentVariant('expert_services_hub_page_keyword_search');
    const { search, results, loading, searchQuery } = useDesignProductSearch(locale);
    const { pageData } = usePage();
    const [hasEngagedSearch, setHasEngagedSearch] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const popularDesignProductResults = useMemo(
        () => getPopularDesignSearchProducts(locale),
        [locale]
    );
    const resultItems = results.length > 0 ? results : popularDesignProductResults;

    const searchResultsDisplayed = isOpen && !loading;
    const prevSearchResultsDisplayed = useRef(false);

    useEffect(() => {
        if (isOpen && !hasEngagedSearch) {
            sendProductSearchEngagedTrackingEvent({
                ...pageData,
                application: 'DesignServicesHubSearch',
                category: 'Internal Search',
                label: 'Design Services:Hub Page',
            });
            setHasEngagedSearch(true);
        }
    }, [hasEngagedSearch, isOpen, pageData]);

    useEffect(() => {
        if (
            searchResultsDisplayed &&
            searchResultsDisplayed !== prevSearchResultsDisplayed.current
        ) {
            sendProductSearchResultsDisplayedTrackingEvent({
                ...pageData,
                ...getProductSearchResultsDisplayedTracking({
                    query: searchQuery,
                    results: resultItems,
                    locale,
                }),
            });
        }

        prevSearchResultsDisplayed.current = searchResultsDisplayed;
    }, [searchResultsDisplayed, searchQuery, resultItems, locale, pageData]);

    return (
        <Banner backgroundColor="accent" data-testid="hero">
            <BannerTextContainer>
                <BannerText>
                    <BannerEyebrow>{__('Design Services')}</BannerEyebrow>
                    <BannerTitle as={H1} fontSkin="title-headline">
                        {__('You take care of your business, we’ll take care of the design')}
                    </BannerTitle>
                    <BannerDescription>
                        <P>
                            {__(
                                'Work with professional, talented designers to bring your brand to life – from handcrafted logos to custom marketing materials and beyond.'
                            )}
                        </P>
                    </BannerDescription>
                    {locale === 'en-US' && inVariant('test') ? (
                        <DesignProductSearch
                            locale={locale}
                            onInputChange={search}
                            inputValue={searchQuery}
                            searchResults={results}
                            loading={loading}
                            minSearchQueryLength={MIN_SEARCH_QUERY_LENGTH}
                            onPopoverOpen={(isPopoverOpen: boolean) => setIsOpen(isPopoverOpen)}
                        />
                    ) : (
                        <BannerLinks>
                            <Button skin="primary" onClick={browseDesignServicesCtaOnClick}>
                                {__('Browse design services')}
                            </Button>
                            <Button skin="secondary" onClick={howItWorksCtaOnClick}>
                                {__('How it works')}
                            </Button>
                        </BannerLinks>
                    )}
                </BannerText>
            </BannerTextContainer>
            <ImageContainer flexDirection="column" justifyContent="center" alignItems="flex-start">
                <CloudinaryImage
                    render={(props) => (
                        <FluidImage
                            src={props.src}
                            srcSet={props.srcSet}
                            alt=""
                            role="presentation"
                        />
                    )}
                    imagePath={__image('expert_services/HubPage/hero-image')}
                    width={686}
                />
            </ImageContainer>
        </Banner>
    );
}
